import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "../context/auth";
import { Button } from "./ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "./ui/dialog";

type Member = {
	id: string;
	name: string;
	email: string;
	role: string;
	createdAt: string;
};

const ROLES = ["viewer", "member", "admin", "owner"] as const;

function formatDate(dateStr: string): string {
	return new Date(dateStr).toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

export function MembersList() {
	const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
	const { user, hasPermission } = useAuth();
	const [members, setMembers] = useState<Member[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [transferTargetId, setTransferTargetId] = useState<string | null>(null);
	const [transferLoading, setTransferLoading] = useState(false);

	const fetchMembers = useCallback(
		async (signal?: AbortSignal) => {
			try {
				setError(null);
				const res = await fetch(`/api/tenant/${workspaceSlug}/members`, {
					credentials: "include",
					signal,
				});
				if (!res.ok) {
					throw new Error(`Failed to load members (${res.status})`);
				}
				const data = (await res.json()) as { members: Member[] };
				setMembers(data.members);
			} catch (err) {
				if (err instanceof DOMException && err.name === "AbortError") return;
				setError(err instanceof Error ? err.message : "Failed to load members");
			} finally {
				setLoading(false);
			}
		},
		[workspaceSlug],
	);

	useEffect(() => {
		const controller = new AbortController();
		fetchMembers(controller.signal);
		return () => controller.abort();
	}, [fetchMembers]);

	async function handleRoleChange(userId: string, newRole: string) {
		try {
			const res = await fetch(
				`/api/tenant/${workspaceSlug}/members/${userId}/role`,
				{
					method: "PATCH",
					credentials: "include",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ role: newRole }),
				},
			);
			if (!res.ok) {
				throw new Error(`Failed to update role (${res.status})`);
			}
			await fetchMembers();
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to update role");
		}
	}

	async function handleRemove(userId: string, name: string) {
		if (!window.confirm(`Remove ${name} from this organization?`)) return;
		try {
			const res = await fetch(
				`/api/tenant/${workspaceSlug}/members/${userId}`,
				{
					method: "DELETE",
					credentials: "include",
				},
			);
			if (!res.ok) {
				throw new Error(`Failed to remove member (${res.status})`);
			}
			await fetchMembers();
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to remove member");
		}
	}

	async function handleTransferOwnership(userId: string) {
		setTransferLoading(true);
		try {
			const res = await fetch(
				`/api/tenant/${workspaceSlug}/members/${userId}/transfer-ownership`,
				{
					method: "POST",
					credentials: "include",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ confirm: true }),
				},
			);
			if (!res.ok) {
				throw new Error(`Failed to transfer ownership (${res.status})`);
			}
			setTransferTargetId(null);
			await fetchMembers();
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Failed to transfer ownership",
			);
		} finally {
			setTransferLoading(false);
		}
	}

	const canTransferOwnership = hasPermission("members", "transfer-ownership");

	if (loading) {
		return (
			<div className="text-sm text-gray-500" data-testid="members-loading">
				Loading members...
			</div>
		);
	}

	if (error) {
		return (
			<div
				className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3"
				data-testid="members-error"
			>
				{error}
			</div>
		);
	}

	const canUpdateRole = hasPermission("members", "update-role");
	const canRemove = hasPermission("members", "remove");

	return (
		<div data-testid="members-list">
			<table className="w-full text-sm text-left">
				<thead>
					<tr className="border-b border-gray-200 text-gray-500 text-xs uppercase tracking-wider">
						<th className="py-2 pr-4 font-medium">Name</th>
						<th className="py-2 pr-4 font-medium">Email</th>
						<th className="py-2 pr-4 font-medium">Role</th>
						<th className="py-2 pr-4 font-medium">Joined</th>
						{(canRemove || canTransferOwnership) && (
							<th className="py-2 font-medium">Actions</th>
						)}
					</tr>
				</thead>
				<tbody>
					{members.map((member) => {
						const isCurrentUser = member.id === user?.id;
						return (
							<tr
								key={member.id}
								className="border-b border-gray-100"
								data-testid="member-row"
							>
								<td className="py-3 pr-4 text-gray-900">{member.name}</td>
								<td className="py-3 pr-4 text-gray-600">{member.email}</td>
								<td className="py-3 pr-4">
									{canUpdateRole && !isCurrentUser ? (
										<select
											value={member.role}
											onChange={(e) =>
												handleRoleChange(member.id, e.target.value)
											}
											className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
											data-testid="role-select"
										>
											{ROLES.map((r) => (
												<option key={r} value={r}>
													{r}
												</option>
											))}
										</select>
									) : (
										<span
											className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
											data-testid="role-badge"
										>
											{member.role}
										</span>
									)}
								</td>
								<td className="py-3 pr-4 text-gray-500">
									{formatDate(member.createdAt)}
								</td>
								{(canRemove || canTransferOwnership) && (
									<td className="py-3 space-x-2">
										{canTransferOwnership &&
											!isCurrentUser &&
											member.role !== "viewer" && (
												<Dialog
													open={transferTargetId === member.id}
													onOpenChange={(open) =>
														setTransferTargetId(open ? member.id : null)
													}
												>
													<DialogTrigger asChild>
														<Button
															variant="outline"
															size="sm"
															data-testid="transfer-ownership-btn"
														>
															Transfer Ownership
														</Button>
													</DialogTrigger>
													<DialogContent>
														<DialogHeader>
															<DialogTitle>Transfer Ownership</DialogTitle>
															<DialogDescription>
																Transfer workspace ownership to{" "}
																<span className="font-semibold">
																	{member.name}
																</span>
																? You will be demoted to admin. This action
																cannot be undone.
															</DialogDescription>
														</DialogHeader>
														<DialogFooter>
															<Button
																variant="outline"
																onClick={() => setTransferTargetId(null)}
															>
																Cancel
															</Button>
															<Button
																variant="destructive"
																disabled={transferLoading}
																onClick={() =>
																	handleTransferOwnership(member.id)
																}
																data-testid="confirm-transfer-btn"
															>
																{transferLoading
																	? "Transferring\u2026"
																	: "Confirm Transfer"}
															</Button>
														</DialogFooter>
													</DialogContent>
												</Dialog>
											)}
										{canRemove && !isCurrentUser && (
											<button
												type="button"
												onClick={() => handleRemove(member.id, member.name)}
												className="text-red-600 hover:text-red-800 text-sm cursor-pointer"
												data-testid="remove-member-btn"
											>
												Remove
											</button>
										)}
									</td>
								)}
							</tr>
						);
					})}
				</tbody>
			</table>
			{members.length === 0 && (
				<p className="text-sm text-gray-400 text-center py-6">
					No members found.
				</p>
			)}
		</div>
	);
}
